// @ts-strict-ignore
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useAtomValue } from 'jotai'
import { FieldErrors, UseFormClearErrors, UseFormRegister, UseFormSetValue } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Avatar, Box, Combobox } from '@node-space/ui-components'
import { useAccountsContext } from 'hooks/context/useAccountsContext'
import { contactAtom } from 'pages/Contacts/atoms/contactAtoms'
import { CURRENCY_RESET_FIELDS } from 'pages/Contacts/constants'
import { ContactFiatInstrumentFormData } from 'pages/Contacts/schemas/contactFiatInstrumentSchema'
import {
  BeneficiaryDetailsCurrency,
  BeneficiaryEntityType,
  BeneficiaryTransferDestination,
  PaymentMethods,
} from 'types/beneficiaries'
import { ContactEntity } from 'types/contacts'

const isZarEnabled = false

type CurrencyDetailsProps = {
  formValues: ContactFiatInstrumentFormData
  errors: FieldErrors<ContactFiatInstrumentFormData>
  isInternationalBankAccount: boolean
  clearErrors: UseFormClearErrors<ContactFiatInstrumentFormData>
  setValue: UseFormSetValue<ContactFiatInstrumentFormData>
  register: UseFormRegister<ContactFiatInstrumentFormData>
}
export const CurrencyDetails = ({
  formValues,
  errors,
  isInternationalBankAccount,
  clearErrors,
  setValue,
  register,
}: CurrencyDetailsProps) => {
  const { t } = useTranslation()
  const hasInitialDataSynced = useRef(false)
  const { accountCapabilities } = useAccountsContext()

  const contact = useAtomValue(contactAtom)

  const [selectedPaymentMethods, setSelectedPaymentMethods] = useState<string[]>(
    formValues?.paymentMethods || []
  )

  // Sync local state when form values are externally updated (e.g., loading existing contact)
  useEffect(() => {
    if (!hasInitialDataSynced.current && formValues?.paymentMethods?.length) {
      setSelectedPaymentMethods(formValues.paymentMethods)
      hasInitialDataSynced.current = true
    }
  }, [formValues?.paymentMethods])

  const handleEurBankCodeSwap = useCallback(
    (previousMethods: string[], newMethods: string[]) => {
      const hadSepa =
        previousMethods?.includes(PaymentMethods.SEPA) ||
        previousMethods?.includes(PaymentMethods.SEPA_INSTANT)
      const hadSwift = previousMethods?.includes(PaymentMethods.SWIFT)
      const hasSepa =
        newMethods?.includes(PaymentMethods.SEPA) ||
        newMethods?.includes(PaymentMethods.SEPA_INSTANT)
      const hasSwift = newMethods?.includes(PaymentMethods.SWIFT)

      if (!newMethods?.length) {
        return
      }

      const isAddingSwiftToSepa = hadSepa && !hadSwift && hasSwift
      const isAddingSepaToSwift = !hadSepa && hadSwift && hasSepa
      const isRemovingSepa = hadSepa && hadSwift && !hasSepa
      const isRemovingSwift = hadSepa && hadSwift && !hasSwift

      if (isAddingSwiftToSepa) {
        setValue('swiftCode', formValues?.bankCode || '')
        setValue('bankCode', '')
        return
      }

      if (isAddingSepaToSwift) {
        setValue('bankCode', formValues?.swiftCode || '')
        setValue('swiftCode', '')
        return
      }

      if (isRemovingSepa) {
        setValue('bankCode', '')
        return
      }

      if (isRemovingSwift) {
        setValue('swiftCode', '')
      }
    },
    [formValues?.currency, formValues?.bankCode, setValue, formValues?.swiftCode]
  )

  const handlePaymentMethodChange = (selectedPaymentMethod: string) => {
    const previousMethods = [...selectedPaymentMethods]
    const newMethods = previousMethods.includes(selectedPaymentMethod)
      ? previousMethods.filter(item => item !== selectedPaymentMethod)
      : [...previousMethods, selectedPaymentMethod]

    if (formValues?.currency === BeneficiaryDetailsCurrency.EUR) {
      handleEurBankCodeSwap(previousMethods, newMethods)
    }
    setSelectedPaymentMethods(newMethods)
  }

  const {
    isCubixBeneficiaryCapabilityEnabled,
    isUsdCRNowBeneficiaryCapabilityEnabled,
    isUsdRtpBeneficiaryCapabilityEnabled,
    isUsdFedNowBeneficiaryCapabilityEnabled,
    isLatamBankAccountCapabilityEnabled,
  } = accountCapabilities || {}
  const isInternational = formValues?.paymentMethods?.includes(
    BeneficiaryTransferDestination.INTERNATIONAL
  )

  const isLocal =
    !isInternational && formValues?.paymentMethods?.includes(BeneficiaryTransferDestination.LOCAL)

  const { currency } = formValues || {}

  const currencies = useMemo(() => {
    const beneficiaryAccountCurrencies: string[] = [
      BeneficiaryDetailsCurrency.EUR,
      BeneficiaryDetailsCurrency.GBP,
      BeneficiaryDetailsCurrency.USD,
      ...(isLatamBankAccountCapabilityEnabled
        ? [
            BeneficiaryDetailsCurrency.MXN,
            BeneficiaryDetailsCurrency.ARS,
            BeneficiaryDetailsCurrency.BRL,
          ]
        : []),
    ]

    if (isLocal && isZarEnabled) {
      beneficiaryAccountCurrencies.push(BeneficiaryDetailsCurrency.ZAR)
    }

    return beneficiaryAccountCurrencies.map(currency => ({
      LeftElement: <Avatar name={currency.toLowerCase()} size="small" />,
      leftText: currency,
      value: currency,
    }))
  }, [isLatamBankAccountCapabilityEnabled, isLocal])

  const paymentMethodsOptions = useMemo(() => {
    switch (formValues?.currency) {
      case BeneficiaryDetailsCurrency.AED:
        return [
          {
            leftText: t('contacts.swift'),
            value: PaymentMethods.SWIFT,
          },
        ]
      case BeneficiaryDetailsCurrency.EUR:
        return [
          {
            leftText: t('contacts.sepaCreditAndInstant'),
            value: PaymentMethods.SEPA,
          },
          {
            leftText: t('contacts.swift'),
            value: PaymentMethods.SWIFT,
          },
        ]
      case BeneficiaryDetailsCurrency.GBP:
        return [
          {
            leftText: t('contacts.fasterPayment'),
            value: PaymentMethods.FASTER_PAYMENT,
          },
          {
            leftText: PaymentMethods.SWIFT,
            value: PaymentMethods.SWIFT,
          },
        ]
      case BeneficiaryDetailsCurrency.USD:
        return [
          {
            leftText: t('contacts.ach'),
            value: PaymentMethods.ACH,
          },
          {
            leftText: t('contacts.fedwire'),
            value: PaymentMethods.FEDWIRE,
          },
          ...(isUsdCRNowBeneficiaryCapabilityEnabled
            ? [
                {
                  leftText: t('contacts.crNow'),
                  value: PaymentMethods.CRNOW,
                },
              ]
            : []),
          ...(isUsdFedNowBeneficiaryCapabilityEnabled
            ? [
                {
                  leftText: t('contacts.fedNow'),
                  value: PaymentMethods.FEDNOW,
                },
              ]
            : []),
          ...(isUsdRtpBeneficiaryCapabilityEnabled
            ? [
                {
                  leftText: t('contacts.rtp'),
                  value: PaymentMethods.RTP,
                },
              ]
            : []),
          {
            leftText: t('contacts.swiftInternationalWire'),
            value: PaymentMethods.SWIFT,
          },
        ]
      case BeneficiaryDetailsCurrency.MXN:
      case BeneficiaryDetailsCurrency.ARS:
      case BeneficiaryDetailsCurrency.BRL:
        return [
          {
            leftText: PaymentMethods.SWIFT,
            value: PaymentMethods.SWIFT,
          },
        ]
      case BeneficiaryDetailsCurrency.ZAR:
      default:
        return []
    }
  }, [
    formValues?.currency,
    isUsdCRNowBeneficiaryCapabilityEnabled,
    isUsdFedNowBeneficiaryCapabilityEnabled,
    isUsdRtpBeneficiaryCapabilityEnabled,
    isCubixBeneficiaryCapabilityEnabled,
    t,
  ])

  const onCurrencyChange = useCallback(
    (value: string) => {
      const isCurrencyChanged = currency && currency !== value && value
      if (isCurrencyChanged) {
        CURRENCY_RESET_FIELDS.forEach(field => {
          setValue(field as keyof ContactFiatInstrumentFormData, '')
          clearErrors(field as keyof ContactFiatInstrumentFormData)
        })
      }

      setValue('currency', value)
      setValue('paymentMethods', [])
      setSelectedPaymentMethods([])
    },
    [contact, currency, paymentMethodsOptions]
  )

  useEffect(() => {
    if (contact?.entity?.type) {
      const entityType =
        contact.entity.type === ContactEntity.COMPANY
          ? BeneficiaryEntityType.BUSINESS
          : BeneficiaryEntityType.INDIVIDUAL
      setValue('entityType', entityType)
    }
  }, [contact?.entity?.type])

  useEffect(() => {
    // If there's only one payment method available, select it by default
    if (paymentMethodsOptions.length === 1 && selectedPaymentMethods.length === 0) {
      setSelectedPaymentMethods([paymentMethodsOptions[0].value])
    }
  }, [paymentMethodsOptions, selectedPaymentMethods])

  useEffect(() => {
    setValue('paymentMethods', selectedPaymentMethods)
  }, [selectedPaymentMethods])

  return (
    <Box width="full">
      <Box
        flexCol
        width="full"
        gapY={16}
        paddingT={24}
        paddingB={isInternationalBankAccount ? null : 12}
      >
        <Box gapY={16} flexCol width="full">
          <Combobox
            {...register('currency')}
            key={formValues?.currency}
            id="currency"
            testId="currency"
            label={t('formFields.bankAccountCurrency')}
            items={currencies}
            errorText={errors?.currency?.message}
            defaultValue={formValues?.currency}
            onChange={value => onCurrencyChange(value)}
            filter={false}
          />

          <Combobox
            {...register('paymentMethods')}
            label={t('contacts.paymentMethods')}
            id="payment-method"
            selectPlaceholder={t('contacts.paymentMethodsPlaceholder')}
            items={paymentMethodsOptions}
            errorText={errors?.paymentMethods?.message}
            filter={false}
            disabled={!formValues?.currency || paymentMethodsOptions?.length === 1}
            checkedValues={selectedPaymentMethods}
            onCheckedChange={handlePaymentMethodChange}
            onChange={() => null}
          />
        </Box>
      </Box>
    </Box>
  )
}
